/**
 * Configuração de runtime vinda de `GET /api/config` (Fase 5 C3).
 *
 * Antes estes flags eram `VITE_*` substituídas estaticamente pelo Vite no build
 * de produção — mudar qualquer um exigia rebuild da imagem. Agora o app os lê no
 * boot e o backend é a fonte única.
 *
 * O módulo mantém defaults sensatos para que qualquer consumidor funcione mesmo
 * antes (ou na ausência) da resposta: nada bloqueia o primeiro render.
 */

export interface ViewerRuntimeConfig {
  publicMaterializedCache: boolean;
  decodedImageCache: "on" | "off";
  decodedImageCacheMb: number;
}

const DEFAULTS: ViewerRuntimeConfig = {
  publicMaterializedCache: true,
  decodedImageCache: "on",
  decodedImageCacheMb: 256,
};

let current: ViewerRuntimeConfig = { ...DEFAULTS };
let loaded = false;

export function viewerConfig(): ViewerRuntimeConfig {
  return current;
}

export function runtimeConfigLoaded(): boolean {
  return loaded;
}

function coerceMode(value: unknown): ViewerRuntimeConfig["decodedImageCache"] {
  const raw = String(value ?? "").toLowerCase();
  return raw === "off" ? "off" : "on";
}

/**
 * Busca a config no backend. Falha é silenciosa e mantém os defaults — o app
 * precisa abrir mesmo se o endpoint estiver indisponível.
 */
export async function loadRuntimeConfig(apiBase: string): Promise<ViewerRuntimeConfig> {
  try {
    const res = await fetch(`${apiBase}/config`, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`config ${res.status}`);
    const body = (await res.json()) as { viewer?: Record<string, unknown> };
    const viewer = body.viewer ?? {};
    current = {
      publicMaterializedCache:
        viewer.public_materialized_cache === undefined
          ? DEFAULTS.publicMaterializedCache
          : Boolean(viewer.public_materialized_cache),
      decodedImageCache: coerceMode(viewer.decoded_image_cache),
      decodedImageCacheMb: Number(viewer.decoded_image_cache_mb) || DEFAULTS.decodedImageCacheMb,
    };
    loaded = true;
  } catch {
    current = { ...DEFAULTS };
  }
  return current;
}

export function __setRuntimeConfigForTests(config: Partial<ViewerRuntimeConfig>): void {
  current = { ...current, ...config };
}
